import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Link,
  Card,
  CardContent,
  CardMedia,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import {
  School,
  Security,
  Brush,
  People,
  Timer,
  Star,
  CheckCircle,
  VideoLibrary,
  Description,
  Assignment
} from "@mui/icons-material";

const Training = () => {
  const theme = useTheme();

  const sections = [
    { id: "overview", title: "Overview" },
    { id: "modules", title: "Training Modules" },
    { id: "videos", title: "Video Resources" },
    { id: "certification", title: "Certification" },
    { id: "faq", title: "FAQ" }
  ];

  const modules = [
    {
      title: "Cleaning Standards & Techniques",
      icon: <Brush />,
      description: "Learn professional cleaning methods, room-by-room checklists, and quality expectations.",
      duration: "45 min",
      level: "Beginner"
    },
    {
      title: "Safety & Hazard Prevention",
      icon: <Security />,
      description: "Chemical safety, slip prevention, fire safety, and emergency procedures.",
      duration: "30 min",
      level: "Beginner"
    },
    {
      title: "Customer Service & Communication",
      icon: <People />,
      description: "How to interact professionally with hosts, handle complaints, and get great reviews.",
      duration: "40 min",
      level: "Intermediate"
    },
    {
      title: "Time Management & Route Planning",
      icon: <Timer />,
      description: "Efficient cleaning sequences, travel optimisation, and punctuality.",
      duration: "25 min",
      level: "Intermediate"
    },
    {
      title: "Advanced Stain & Surface Care",
      icon: <Star />,
      description: "Specialised techniques for carpets, upholstery, hardwood, and delicate surfaces.",
      duration: "60 min",
      level: "Advanced"
    }
  ];

  const videoResources = [
    { title: "How to clean a bathroom professionally", duration: "8:42", link: "#" },
    { title: "Kitchen deep cleaning step by step", duration: "12:15", link: "#" },
    { title: "Using eco-friendly cleaning products", duration: "6:30", link: "#" },
    { title: "Dealing with difficult customers", duration: "10:22", link: "#" },
    { title: "Spotting safety hazards before you start", duration: "7:55", link: "#" }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Chip
          label="Cleaners"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" }
          }}
        >
          Training Center
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Elevate your cleaning skills, earn certifications, and become a top‑rated cleaner on FreshSweeper.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 100,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.8),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              On this page
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, listStyle: "none" }}>
              {sections.map((section) => (
                <Box component="li" key={section.id} sx={{ mb: 0.5 }}>
                  <Link
                    href={`#${section.id}`}
                    underline="hover"
                    sx={{
                      fontSize: "0.85rem",
                      color: theme.palette.text.secondary,
                      "&:hover": { color: theme.palette.primary.main },
                      textDecoration: "none"
                    }}
                  >
                    {section.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            {/* Overview */}
            <Box id="overview" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Welcome to the FreshSweeper Training Center
              </Typography>
              <Typography variant="body1" paragraph>
                At FreshSweeper, we believe that great cleaning comes from continuous learning. 
                Our training programs are designed to help you deliver exceptional service, 
                stay safe on the job, and build a reputation that earns you more bookings and higher ratings.
              </Typography>
              <Typography variant="body1" paragraph>
                All training modules are completely free and self‑paced. Complete the full curriculum 
                to earn your <strong>FreshSweeper Certified Cleaner</strong> badge – a mark of excellence 
                that will appear on your profile and help you stand out to hosts.
              </Typography>
              <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2, mt: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  📈 Certified cleaners earn on average 35% more bookings and 4.9-star ratings.
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Training Modules */}
            <Box id="modules" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Training Modules
              </Typography>
              <Grid container spacing={2}>
                {modules.map((mod, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Card sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 3 }}>
                      <Box sx={{ mr: 2, color: theme.palette.primary.main }}>
                        {mod.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {mod.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {mod.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Chip label={`⏱️ ${mod.duration}`} size="small" variant="outlined" />
                          <Chip label={mod.level} size="small" color="primary" />
                        </Box>
                      </Box>
                      <Button variant="contained" sx={{ ml: 2 }}>Start</Button>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Video Resources */}
            <Box id="videos" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Video Resources
              </Typography>
              <Typography variant="body1" paragraph>
                Watch short, practical videos that demonstrate key techniques and best practices.
              </Typography>
              <List>
                {videoResources.map((video, idx) => (
                  <ListItem key={idx} component={Link} href={video.link} sx={{ textDecoration: 'none', color: 'inherit' }}>
                    <ListItemIcon>
                      <VideoLibrary color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={video.title} secondary={`Duration: ${video.duration}`} />
                    <Button size="small" variant="outlined">Watch</Button>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Certification */}
            <Box id="certification" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Certification Program
              </Typography>
              <Typography variant="body1" paragraph>
                When you complete all core modules and pass a short assessment, you’ll receive:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="FreshSweeper Certified Cleaner badge on your profile" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Priority in search results for hosts looking for certified cleaners" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Access to exclusive higher‑paying job postings" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText primary="Digital certificate you can share with clients" />
                </ListItem>
              </List>
              <Button variant="contained" startIcon={<Assignment />} sx={{ mt: 2 }}>
                Take the Certification Assessment
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* FAQ */}
            <Box id="faq" sx={{ scrollMarginTop: 80, mb: 2 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Frequently Asked Questions
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Is training free?</Typography>
                <Typography variant="body2" color="text.secondary">Yes, all training modules and videos are completely free for registered cleaners.</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>How long does certification take?</Typography>
                <Typography variant="body2" color="text.secondary">Most cleaners complete the five core modules in 3–5 hours, depending on your pace.</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Do I have to get certified?</Typography>
                <Typography variant="body2" color="text.secondary">Certification is optional, but highly recommended. Certified cleaners receive significantly more booking requests and higher ratings.</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>Will my progress be saved?</Typography>
                <Typography variant="body2" color="text.secondary">Yes, your progress is automatically saved. You can resume anytime from the Training Center.</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Training;